import { useEffect, useState } from "react";
import { Drawer, Menu } from "antd";
import GOVCO from "../../../assets/landing/Logo.png";
import MedellinLogo from "../../../assets/landing/medellin.png";
import "./header.style.scss";
import { useNavigate } from "react-router";
import { NavLink, useLocation } from "react-router-dom";
import {
   FaBars,
   FaHome,
   FaUsers,
   FaCalendarAlt,
   FaChalkboardTeacher,
   FaClipboardList,
   FaVideo,
   FaBookOpen,
   FaHandPointUp,
} from "react-icons/fa";
import { BsPerson, BsPersonX, BsPersonPlus } from "react-icons/bs";
import { NavDrawer } from "./NavDrawer";
import getStorageUser from "../../../middlewares/GetStorageUser";

export const Header = () => {
   const navigate = useNavigate();
   const location = useLocation();
   const [isLogged, setIsLogged] = useState<boolean>(false);
   const [userName, setUserName] = useState<string>("");
   const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
   const [fontSizeIncreased, setFontSizeIncreased] = useState<boolean>(false);
   const [highContrast, setHighContrast] = useState<boolean>(false);

   const { SubMenu } = Menu;
   const isPPSection = location.pathname.startsWith('/pp');

   useEffect(() => {
      const user = localStorage.getItem('user');
      if (user) {
         setIsLogged(true);
         try {
            const u = getStorageUser();
            if (u && u.nombres) {
               const primer = u.nombres.split(" ")[0];
               setUserName(primer.charAt(0).toUpperCase() + primer.slice(1).toLowerCase());
            }
         } catch (e) {}
      }
   }, [localStorage.token]);

   const goLogin = (): void => {
      navigate("/login");
   };

   const handleLogout = (): void => {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.clear();
      setIsLogged(false);
      window.location.replace('https://www.medellin.gov.co/es/secretaria-cultura-ciudadana/estimulos-cultura/');
   };

   const onNavigateDrawer = (): void => {
      setDrawerOpen(false);
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
   };

   const toggleFontSize = () => {
      setFontSizeIncreased(!fontSizeIncreased);
      document.body.classList.toggle("font-size-accessible");
   };

   const toggleContrast = () => {
      setHighContrast(!highContrast);
      document.body.classList.toggle("high-contrast-mode");
   };

   // Identifica la opción activa del menú según la ruta actual
   const getCurrentMenuKey = (): string => {
      const path = location.pathname;
      if (path === "/" || path === "/estimuloscultura" || path === "/estimuloscultura/") return "convocatorias";
      if (path.includes("/beneficiarios")) return "beneficiarios";
      if (path.includes("/agenda")) return "agenda";
      if (path.includes("/mentorias")) return "mentorias";
      if (path.includes("/form")) return "form";
      if (path.includes("/miradasProgramacion")) return "miradas-programacion";
      if (path.includes("/miradasIndustria")) return "miradas-industria";
      if (path.includes("/miradasPrensa")) return "miradas-prensa";
      if (path.includes("/cinematecaDistrital")) return "miradas-cinemateca";
      if (path.includes("/miradas")) return "miradas-home";
      if (path.includes("/lep")) return "lep";
      if (path.includes("/ppestimulos")) return "ppestimulos";
      if (path.includes("/ppformacion")) return "ppformacion";
      if (path.includes("/ppfortalecimiento")) return "ppfortalecimiento";
      if (path.includes("/ppagenda")) return "ppagenda";
      if (path.includes("/pp")) return "pp";
      return "convocatorias";
   };

   const currentKey = getCurrentMenuKey();

   return (
      <header className="alcaldia-header-wrapper sticky">
         {/* Barra superior azul gubernamental con GOV.CO (sin la M) */}
         <div className="alcaldia-top-bar">
            <div className="top-bar-container">
               <div className="top-bar-left">
                  <a
                     href="https://www.gov.co"
                     target="_blank"
                     rel="noopener noreferrer"
                     className="govco-link"
                     title="Portal del Estado Colombiano GOV.CO"
                  >
                     <img src={GOVCO} alt="GOV.CO" className="govco-logo" />
                  </a>
                  <span className="top-bar-divider">|</span>
                  <div className="alcaldia-brand-badge">
                     <span className="badge-text">
                        Alcaldía de Medellín <span className="bullet">·</span> Distrito Especial de Ciencia, Tecnología e Innovación
                     </span>
                  </div>
               </div>

               <div className="top-bar-right">
                  <button
                     type="button"
                     className={`accessibility-btn ${fontSizeIncreased ? 'active' : ''}`}
                     onClick={toggleFontSize}
                     title="Aumentar tamaño de fuente"
                     aria-label="Aumentar tamaño de fuente"
                  >
                     A+
                  </button>
                  <span className="top-bar-divider">|</span>
                  <button
                     type="button"
                     className={`accessibility-btn contrast-btn ${highContrast ? 'active' : ''}`}
                     onClick={toggleContrast}
                     title="Contraste"
                     aria-label="Modo alto contraste"
                  >
                     <span className="contrast-icon" />
                  </button>
               </div>
            </div>
         </div>

         {/* Barra principal blanca: Menú hamburguesa a la izquierda junto al logo */}
         <div className="alcaldia-main-navbar">
            <div className="main-navbar-container">
               <div className="navbar-logo-area">
                  <button
                     type="button"
                     className="navbar-hamburger-btn"
                     onClick={() => setDrawerOpen(true)}
                     title="Abrir menú"
                     aria-label="Abrir menú"
                  >
                     <FaBars />
                  </button>
                  <NavLink to="/" className="navbar-logo-card" title="Alcaldía de Medellín">
                     <img
                        src={MedellinLogo}
                        alt="Alcaldía de Medellín - Distrito de Ciencia, Tecnología e Innovación"
                        className="medellin-logo-img"
                     />
                  </NavLink>
               </div>

               <div className="navbar-actions-area">
                  {isLogged ? (
                     <div className="user-session-group">
                        <button
                           type="button"
                           className="navbar-login-btn profile-btn"
                           onClick={() => navigate("/usuario")}
                           title="Mi panel de usuario"
                        >
                           <BsPerson style={{ marginRight: 6 }} />
                           {userName ? userName : "Inicio"}
                        </button>
                        <button
                           type="button"
                           className="logout-link-btn"
                           onClick={handleLogout}
                           title="Cerrar sesión"
                        >
                           <BsPersonX style={{ marginRight: 4 }} />
                           Cerrar sesión
                        </button>
                     </div>
                  ) : (
                     <button
                        type="button"
                        className="navbar-login-btn"
                        onClick={goLogin}
                     >
                        Iniciar sesión
                     </button>
                  )}
               </div>
            </div>
         </div>

         {/* Drawer de Navegación en el lado izquierdo */}
         <NavDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      </header>
   );
};


